import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'

import { useTheme } from 'styled-components/native'

import { RefreshIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/RefreshIcon'
import { ScheduleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ScheduleIcon'
import { TrashIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/TrashIcon'
import { HoverActionItem } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/hoverActions/HoverActions'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import { ClientRegistration } from '../../../../../addNewClients/data/addNewClientApi'
import { LC_CALENDAR_CURRENT } from '../../../../../common/constants/routingConstants'

type UseNewClientHoverActionsArgs = {
  onDeletePress: (reg: ClientRegistration) => void
  onResendPress: (reg: ClientRegistration) => void
}

export const useNewClientHoverActions = ({
  onDeletePress,
  onResendPress,
}: UseNewClientHoverActionsArgs): ((reg: ClientRegistration) => HoverActionItem[]) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { colors } = useTheme()

  return useCallback(
    (reg: ClientRegistration): HoverActionItem[] => {
      const resendAction: HoverActionItem = {
        id: 'resend',
        icon: <RefreshIcon fillColor={colors.iconDefault} />,
        label: intl.formatMessage({
          defaultMessage: 'Resend registration link',
          description: 'Hover action label for resending a registration invite',
        }),
        onPress: () => onResendPress(reg),
        testId: tID(`ClientInvitationRow-resend-${reg.id}`),
      }

      const scheduleAction: HoverActionItem = {
        id: 'schedule',
        icon: <ScheduleIcon fillColor={colors.iconDefault} />,
        label: intl.formatMessage({
          defaultMessage: 'Schedule',
          description: 'Hover action label for scheduling a session with a client',
        }),
        onPress: () => {
          track({ event: mixpanelEvents.ADD_NEW_CLIENT_SCHEDULE })
          navigate(LC_CALENDAR_CURRENT.route)
        },
        testId: tID(`ClientInvitationRow-schedule-${reg.id}`),
      }

      const deleteAction: HoverActionItem = {
        id: 'delete',
        icon: <TrashIcon fillColor={colors.iconDefault} />,
        label: intl.formatMessage({
          defaultMessage: 'Remove',
          description: 'Hover action label for removing a client registration',
        }),
        onPress: () => onDeletePress(reg),
        testId: tID(`ClientInvitationRow-delete-${reg.id}`),
      }

      switch (reg.registration_status) {
        case 'pending':
          return [deleteAction]
        case 'expired':
          return [resendAction, deleteAction]
        case 'completed':
          return [scheduleAction, deleteAction]
        default:
          return []
      }
    },
    [intl, navigate, colors, onResendPress, onDeletePress],
  )
}
